import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { fmtTime } from "@/lib/format";
import { agendaKey, agendaStyle, isDone } from "@/lib/agendaColors";

const HOUR = 48;
const DAY_MS = 86400000;
const dayLabel = new Intl.DateTimeFormat("it-IT", { weekday: "short", day: "2-digit" });

const layout = (events) => {
  const lanes = [];
  const placed = events.map((e) => {
    const s = new Date(e.start_at).getTime();
    let lane = lanes.findIndex((end) => end <= s);
    if (lane === -1) { lane = lanes.length; lanes.push(0); }
    lanes[lane] = new Date(e.end_at).getTime();
    return { e, lane };
  });
  return placed.map((p) => ({ ...p, count: lanes.length }));
};

const EventBlock = ({ e, lane, count, dayStart, onOpen, drag }) => {
  const top = Math.max(0, (new Date(e.start_at) - dayStart) / 3600000) * HOUR;
  const height = Math.max(22, ((new Date(e.end_at) - new Date(e.start_at)) / 3600000) * HOUR - 2);
  const movable = !!drag && drag.can(e);
  return (
    <button onClick={(ev) => { ev.stopPropagation(); onOpen(e); }} data-testid={`week-event-${e.id}`} data-kind={agendaKey(e)}
      draggable={movable} onDragStart={(ev) => { drag.start(e, ev.clientY - ev.currentTarget.getBoundingClientRect().top); ev.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => drag.end()}
      style={{ top, height, left: `${(lane / count) * 100}%`, width: `${100 / count}%` }}
      className={cn("absolute overflow-hidden rounded-md border-l-[3px] px-1.5 py-1 text-left text-[11px] leading-tight transition-colors",
        agendaStyle(e).block, isDone(e) && "opacity-60", movable && "cursor-grab active:cursor-grabbing")}>
      <p className={cn("truncate font-semibold text-ink", isDone(e) && "line-through")}>{e.subject}</p>
      <p className="truncate text-slate-500">{fmtTime(e.start_at)}{e.location ? ` · ${e.location}` : ""}</p>
    </button>
  );
};

export const WeekGrid = ({ from, items, onOpen, onCreate, drag }) => {
  const scroller = useRef(null);
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = 7 * HOUR; }, [from]);
  const days = Array.from({ length: 7 }, (_, i) => new Date(from.getTime() + i * DAY_MS));
  const today = new Date().toDateString();
  const timed = items.filter((e) => !e.is_all_day);
  return (
    <div className="overflow-x-auto" data-testid="calendar-week-grid">
      <div className="min-w-[760px]">
        <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-line">
          <div />
          {days.map((d) => (
            <div key={d.toISOString()} className={cn("px-2 py-2 text-center text-xs font-semibold capitalize",
              d.toDateString() === today ? "text-brand-700" : "text-slate-500")}>{dayLabel.format(d)}</div>
          ))}
        </div>
        <div ref={scroller} className="max-h-[620px] overflow-y-auto">
          <div className="relative grid grid-cols-[56px_repeat(7,1fr)]" style={{ height: 24 * HOUR }}>
            <div>{Array.from({ length: 24 }, (_, h) => (
              <div key={h} style={{ height: HOUR }} className="pr-2 text-right font-mono text-[10px] text-slate-400">{h ? `${String(h).padStart(2, "0")}:00` : ""}</div>
            ))}</div>
            {days.map((d) => {
              const dayEvents = timed.filter((e) => new Date(e.start_at).toDateString() === d.toDateString());
              return (
                <div key={d.toISOString()} className={cn("relative border-l border-line", d.toDateString() === today && "bg-brand-50/30")}
                  onDragOver={(ev) => drag?.active() && ev.preventDefault()}
                  onDrop={(ev) => { ev.preventDefault(); const min = (ev.clientY - ev.currentTarget.getBoundingClientRect().top - drag.offset()) / HOUR * 60; drag.drop(new Date(d.getTime() + Math.max(0, Math.round(min / 15) * 15) * 60000)); }}
                  onClick={(ev) => onCreate && onCreate(new Date(d.getTime() + Math.floor((ev.clientY - ev.currentTarget.getBoundingClientRect().top) / HOUR) * 3600000))}>
                  {Array.from({ length: 24 }, (_, h) => <div key={h} style={{ height: HOUR }} className="border-b border-line/60" />)}
                  {layout(dayEvents).map((p) => <EventBlock key={p.e.id} {...p} dayStart={d} onOpen={onOpen} drag={drag} />)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
